import { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';

import { Loader } from 'common/components/Loader';
import { FilterBar } from 'common/components/FilterBar';
import { Button } from 'common/components/Button';
import {
    ITrainingListItemFragment,
    LtiDeepLinkTrainingsDocument,
} from 'graphql/types';
import { ContentContainer } from 'common/components/ContentContainer';
import { TrainingAddList } from 'training/components/TrainingAddList';
import { PageTitle } from 'common/components/PageTitle';

interface IProps {
    launchId: string;
    loadingDeeplink?: boolean;
    onSelectTrainings(selectedTrainings: ITrainingListItemFragment[]): void;
}

export const LtiDeepLinkTrainings = ({
    launchId,
    loadingDeeplink,
    onSelectTrainings,
}: IProps) => {
    const [translate] = useTranslation();
    const [selectedTrainings, setSelectedTrainings] = useState<
        ITrainingListItemFragment[]
    >([]);
    const [searchValue, setSearchValue] = useState('');

    const { data, loading } = useQuery(LtiDeepLinkTrainingsDocument, {
        variables: { launchId },
    });

    // Simple frontend search — the backend already filters by LTI platform config
    const trainings = useMemo(() => {
        const allTrainings = data?.ltiDeepLinkTrainings ?? [];

        if (!searchValue) return allTrainings;

        const query = searchValue.toLowerCase();

        return allTrainings.filter((training) =>
            training.title.toLowerCase().includes(query)
        );
    }, [data?.ltiDeepLinkTrainings, searchValue]);

    const handleSelectTraining = (training: ITrainingListItemFragment) => {
        const newTrainings = [...selectedTrainings, training];

        setSelectedTrainings(newTrainings);
    };

    const handleRemoveTraining = (training: ITrainingListItemFragment) => {
        const newSelectedTrainings = selectedTrainings.filter(
            (selectedTraining) => selectedTraining.id !== training.id
        );

        setSelectedTrainings(newSelectedTrainings);
    };

    const handleSearch = (value: string) => {
        setSearchValue(value);
    };

    return (
        <>
            <PageTitle>
                {translate('lTIDeeplinkPage.trainingsPageTitle')}
            </PageTitle>
            <Box sx={{ pb: 22.5, pt: 4 }}>
                <FilterBar
                    initialSearchValue={searchValue}
                    placeholder={translate('filterBarPlaceholder')}
                    onSearch={handleSearch}
                    onSearchClear={() => handleSearch('')}
                />

                {loading && <Loader />}

                {!loading && (
                    <TrainingAddList
                        addedTrainings={selectedTrainings}
                        selectedTrainings={selectedTrainings}
                        trainings={trainings}
                        onAddTraining={handleSelectTraining}
                        onRemoveTraining={(
                            selectedTraining: ITrainingListItemFragment
                        ) => handleRemoveTraining(selectedTraining)}
                    />
                )}
            </Box>

            <Box
                sx={{
                    position: 'fixed',
                    left: 0,
                    bottom: 0,
                    width: '100%',
                    backgroundColor: 'grey.100',
                    p: 2,
                }}
            >
                <ContentContainer maxWidth="md" sx={{ pb: 0 }}>
                    <Button
                        color="primary"
                        disabled={!selectedTrainings.length}
                        loading={loadingDeeplink}
                        sx={{ width: '100%' }}
                        variant="contained"
                        onClick={() => {
                            onSelectTrainings(selectedTrainings);
                            setSelectedTrainings([]);
                            setSearchValue('');
                        }}
                    >
                        {!selectedTrainings.length
                            ? translate('addTrainingsTitle')
                            : translate('addTrainings', {
                                  count: selectedTrainings.length,
                              })}
                    </Button>
                </ContentContainer>
            </Box>
        </>
    );
};
